import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft,
  Phone, Loader2, FileText, Download, Eye,
  RefreshCw, MessageCircle, Check, Printer
} from 'lucide-react';
import { message, Tooltip } from 'antd';
import { maskPhone, formatDate } from '../utils/helpers';
import { invoiceApi, companyProfileApi, ASSET_BASE_URL as ASSET_URL } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import * as XLSX from 'xlsx';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import ThemeSelect from '../components/ThemeSelect';
import ExcelExportButton from '../components/ExcelExportButton';
import Pagination from '../components/Pagination';

// ─────────────────────────────────────────────────────────────────────────────
// InvoiceDetail Component — Tax Invoice View
// ─────────────────────────────────────────────────────────────────────────────
function InvoiceDetail({ invoice, onBack, onDownload, currentTheme, isDark }) {
  if (!invoice) return null;
  const d = invoice;

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      {/* ── Top nav ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all ${
              isDark
                ? 'bg-white/5 border-white/10 hover:bg-white/10 text-white'
                : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <FileText className="w-5 h-5" style={{ color: currentTheme.color }} />
              Invoice Detail
            </h1>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Invoice #{d.invoiceNo || '—'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className={`px-4 py-2.5 rounded-xl border text-sm font-semibold flex items-center gap-2 transition-all ${
              isDark
                ? 'bg-white/5 border-white/10 hover:bg-white/10 text-white'
                : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700 shadow-sm'
            }`}
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={onDownload}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold flex items-center gap-2 shadow-md transition-all active:scale-95"
            style={{ backgroundColor: currentTheme.color }}
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </div>
      </div>

      {/* ── Invoice Card ── */}
      <div className={`rounded-2xl border shadow-sm overflow-hidden ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-200'}`}>
        {/* ══ HEADER ══ */}
        <div className="p-6 md:p-8 border-b border-gray-100 dark:border-white/5">
          {/* Row 1: Logo | TAX INVOICE | Tagline + Date */}
          <div className="flex flex-col md:flex-row items-start justify-between gap-6 mb-6">
            {/* LEFT: Logo + GSTIN/FSSAI */}
            <div className="flex flex-col gap-1 min-w-[220px]">
              {d.company?.logoUrl ? (
                <img
                  src={d.company.logoUrl}
                  alt="Company Logo"
                  className="h-16 object-contain object-left mb-1"
                />
              ) : (
                <div
                  className="text-2xl md:text-3xl font-black leading-tight mb-1"
                  style={{ color: currentTheme.color }}
                >
                  {d.company?.name || 'Company'}
                </div>
              )}
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium`}>
                {d.company?.gstin ? `GSTIN: ${d.company.gstin}` : ''}
                {d.company?.gstin && d.company?.fssai ? ' | ' : ''}
                {d.company?.fssai ? `FSSAI: ${d.company.fssai}` : ''}
              </p>
            </div>

            {/* MIDDLE: TAX INVOICE + Invoice # */}
            <div className="flex flex-col gap-1 md:text-center flex-1">
              <span className={`text-2xl font-black tracking-wider uppercase ${isDark ? 'text-white' : 'text-gray-900'}`}>
                TAX INVOICE
              </span>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Invoice #:&nbsp;<span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{d.invoiceNo || '—'}</span>
              </p>
            </div>

            {/* RIGHT: Tagline + Date */}
            <div className="flex flex-col md:items-end gap-1 flex-shrink-0">
              {d.company?.tagLine && (
                <p className="text-sm font-bold" style={{ color: currentTheme.color }}>
                  {d.company.tagLine}
                </p>
              )}
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Invoice date:&nbsp;
                <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{d.invoiceDate || '—'}</span>
              </p>
            </div>
          </div>

          {/* Row 2: Company Info | Billed To | Shipped To */}
          <div className={`grid grid-cols-1 md:grid-cols-3 border rounded-xl overflow-hidden ${isDark ? 'border-white/10 divide-y md:divide-y-0 md:divide-x divide-white/10 bg-white/[0.02]' : 'border-gray-200 divide-y md:divide-y-0 md:divide-x divide-gray-200 bg-gray-50/50'}`}>
            {/* Company address */}
            <div className="p-4 space-y-2">
              <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider">From</p>
              <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                {d.company?.name || 'Company'}
              </p>
              {d.company?.address && (
                <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                  📍 {d.company.address}
                </p>
              )}
              {d.company?.phone && (
                <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  📞 {d.company.phone}
                </p>
              )}
              {d.company?.email && (
                <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  ✉️ {d.company.email}
                </p>
              )}
            </div>

            {/* Billed To */}
            <div className="p-4 space-y-2">
              <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider">Billed To</p>
              <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{d.billedTo?.name || '—'}</p>
              {d.billedTo?.phone && (
                <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  📞 {d.billedTo.phone}
                </p>
              )}
              {d.billedTo?.address && (
                <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                  📍 {d.billedTo.address}
                </p>
              )}
            </div>

            {/* Shipped To */}
            <div className="p-4 space-y-2">
              <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider">Shipped To</p>
              <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{d.shippedTo?.name || d.billedTo?.name || '—'}</p>
              {d.shippedTo?.phone && (
                <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  📞 {d.shippedTo.phone}
                </p>
              )}
              {d.shippedTo?.address && (
                <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                  📍 {d.shippedTo.address}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ══ ITEMS TABLE ══ */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: `${currentTheme.color}15` }}>
                {['S. No.', 'Product Description', 'Unit Price', 'Quantity', 'Discount', 'Total'].map(h => (
                  <th
                    key={h}
                    className="px-6 py-3.5 text-left text-xs font-bold uppercase tracking-wider"
                    style={{ color: currentTheme.color }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
              {(d.items || []).map((item, idx) => (
                <tr key={idx} className={`transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'}`}>
                  <td className={`px-6 py-4 text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{idx + 1}</td>
                  <td className={`px-6 py-4 text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {item.productName || item.product || '—'}
                  </td>
                  <td className={`px-6 py-4 text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    ₹ {Number(item.unitPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`px-6 py-4 text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    {item.quantity || item.qty || 1}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-emerald-600">
                    ₹ {Number(item.discount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-6 py-4 text-sm font-bold" style={{ color: currentTheme.color }}>
                    ₹ {Number(item.totalPrice || item.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
              {(!d.items || d.items.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-400">
                    No items in this invoice
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ══ FOOTER: Terms + Signature LEFT | Totals RIGHT ══ */}
        <div className={`grid grid-cols-1 md:grid-cols-2 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
          {/* Terms + Signature */}
          <div className={`p-6 md:p-8 border-b md:border-b-0 md:border-r ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
            <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Terms & Conditions:
            </p>
            <ol className="list-decimal list-inside space-y-1.5 text-xs text-gray-500 leading-relaxed mb-6">
              {(d.terms || []).map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ol>

            {/* Signature block */}
            <div className="pt-4 border-t border-dashed border-gray-200 dark:border-white/10 flex flex-col gap-1">
              <p className="text-xs text-gray-400">
                For <strong className={isDark ? 'text-gray-200' : 'text-gray-700'}>{d.company?.name || 'Company'}</strong>
              </p>
              <div className="h-14 flex items-center py-1">
                {d.company?.signatureUrl ? (
                  <img
                    src={d.company.signatureUrl}
                    alt="Authorized Signatory"
                    className="h-12 object-contain"
                  />
                ) : (
                  <span className="text-2xl italic text-gray-400 font-serif">
                    Authorized Signatory
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-400 uppercase font-semibold">Authorized Signatory</p>
            </div>
          </div>

          {/* Totals */}
          <div className="p-6 md:p-8 flex flex-col justify-between bg-gray-50/50 dark:bg-white/[0.01]">
            <div className="space-y-3 w-full max-w-sm ml-auto">
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-white/10 text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  ₹ {Number(d.subTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {Number(d.advancePayable || 0) > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-white/10 text-sm">
                  <span className="text-gray-500">Advance Paid</span>
                  <span className="font-semibold text-emerald-600">
                    - ₹ {Number(d.advancePayable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-white/10 text-sm">
                <span className="text-gray-500">
                  Taxable Amount
                  <span className="block text-[11px] text-gray-400">(GST @{d.gstPercentage || 18}%)</span>
                </span>
                <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  ₹ {Number(d.grandTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>

              <div className="flex justify-between items-center pt-2">
                <span className={`text-base font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>To Pay:</span>
                <span className="text-2xl font-black" style={{ color: currentTheme.color }}>
                  ₹ {Number(d.pendingAmount !== undefined ? d.pendingAmount : (d.grandTotal || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Status Badge Component
// ─────────────────────────────────────────────────────────────────────────────
function StatusBadge({ status, isDark }) {
  const s = String(status || '').toUpperCase();
  const styleMap = {
    PROCESSING:       isDark ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-amber-50 text-amber-600 border-amber-200',
    READY_TO_SHIP:    isDark ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-amber-50 text-amber-600 border-amber-200',
    DISPATCHED:       isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200',
    IN_TRANSIT:       isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200',
    OUT_FOR_DELIVERY: isDark ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-purple-50 text-purple-600 border-purple-200',
    DELIVERED:        isDark ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-50 text-green-600 border-green-200',
    CANCELLED:        isDark ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-500 border-red-200',
    RTO:              isDark ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-500 border-red-200',
    NDR:              isDark ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'bg-orange-50 text-orange-600 border-orange-200',
    HOLD:             isDark ? 'bg-white/10 text-gray-400 border-white/20' : 'bg-gray-100 text-gray-500 border-gray-200',
    GENERATED:        isDark ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-50 text-green-600 border-green-200',
    SENT:             isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200',
    PAID:             isDark ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-50 text-green-700 border-green-200',
  };
  const labelMap = {
    PROCESSING: 'Processing', READY_TO_SHIP: 'Ready to ship',
    DISPATCHED: 'Dispatched', IN_TRANSIT: 'In Transit',
    OUT_FOR_DELIVERY: 'Out for Delivery', DELIVERED: 'Delivered',
    CANCELLED: 'Cancelled', RTO: 'RTO', NDR: 'NDR', HOLD: 'On Hold',
    GENERATED: 'Generated', SENT: 'Sent', PAID: 'Paid',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold border whitespace-nowrap ${styleMap[s] || (isDark ? 'bg-white/5 text-gray-400 border-white/10' : 'bg-gray-100 text-gray-600 border-gray-200')}`}>
      {labelMap[s] || status || '—'}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main BillingList Component
// ─────────────────────────────────────────────────────────────────────────────
function BillingList() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);

  // Invoice view state
  const [invoiceView, setInvoiceView] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Checkbox selection
  const [selectedRows, setSelectedRows] = useState(new Set());

  const pageSizeOptions = [10, 20, 50, 100];

  const toggleRow = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fetchData = useCallback(async (page, search, size) => {
    try {
      setLoading(true);
      setError(null);

      const targetPage = page !== undefined ? page : currentPage;
      const targetSearch = search !== undefined ? search : searchTerm;
      const targetSize = size !== undefined ? size : pageSize;

      const [billingData, profileData] = await Promise.allSettled([
        invoiceApi.getAllInvoices({
          page: targetPage - 1,
          size: targetSize,
          search: targetSearch?.trim() || undefined,
        }),
        companyProfileApi.getDefaultProfile(),
      ]);

      const rawBilling = billingData.status === 'fulfilled' ? billingData.value : null;
      let list = [];
      let total = 0;

      if (Array.isArray(rawBilling)) {
        list = rawBilling;
        total = rawBilling.length;
      } else if (rawBilling && typeof rawBilling === 'object') {
        list  = rawBilling.content || rawBilling.data || rawBilling.orders || [];
        total = rawBilling.totalElements || rawBilling.total || (rawBilling.totalPages ? rawBilling.totalPages * targetSize : list.length);
      }

      setInvoices(list);
      setTotalInvoices(total);

      if (profileData.status === 'fulfilled' && profileData.value) {
        setCompanyProfile(profileData.value);
      } else {
        try {
          const profiles = await companyProfileApi.getActiveProfiles();
          const arr = Array.isArray(profiles) ? profiles : profiles?.content || profiles?.data || [];
          if (arr.length > 0) setCompanyProfile(arr[0]);
        } catch (_) {}
      }
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to load billing list.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, pageSize]);

  useEffect(() => {
    fetchData(currentPage, searchTerm, pageSize);
  }, [currentPage, pageSize, searchTerm, fetchData]);

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    setCurrentPage(1);
    fetchData(1, val, pageSize);
  };

  const handlePageSizeChange = (val) => {
    const newSize = parseInt(val, 10) || 20;
    setPageSize(newSize);
    setCurrentPage(1);
    fetchData(1, searchTerm, newSize);
  };

  const handleRefresh = () => {
    fetchData(currentPage, searchTerm, pageSize);
  };

  // Checkbox helpers
  const toggleAll = () => {
    if (selectedRows.size === invoices.length && invoices.length > 0) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(invoices.map(i => i.id)));
    }
  };

  const isAllSelected = invoices.length > 0 && invoices.every(i => selectedRows.has(i.id));

  const getProfileId = async () => {
    if (companyProfile?.id || companyProfile?.companyId) {
      return companyProfile.id || companyProfile.companyId;
    }
    try {
      const def = await companyProfileApi.getDefaultProfile();
      if (def?.id) {
        setCompanyProfile(def);
        return def.id;
      }
    } catch (_) {}
    try {
      const profiles = await companyProfileApi.getAllProfiles();
      const arr = Array.isArray(profiles) ? profiles : profiles?.content || profiles?.data || [];
      if (arr.length > 0) {
        setCompanyProfile(arr[0]);
        return arr[0].id;
      }
    } catch (_) {}
    return null;
  };

  // ── Single Invoice PDF Download ──
  const handleSingleDownload = async (item) => {
    const rowId = item.id || item.orderId;
    const targetOrderId = item.orderId || item.id;
    const invoiceNum = item.invoiceNumber || item.invoiceNo || targetOrderId;

    try {
      message.loading({ content: `Downloading invoice #${invoiceNum}...`, key: `dl_${rowId}`, duration: 0 });

      let blob;
      try {
        blob = await invoiceApi.downloadByOrderId(targetOrderId);
      } catch (err) {
        const isNotFound = err.message && (
          err.message.toLowerCase().includes('not found') ||
          err.message.includes('400') ||
          err.message.includes('404')
        );

        if (isNotFound) {
          const profileId = await getProfileId();
          try {
            message.loading({ content: `Generating invoice for Order #${targetOrderId}...`, key: `dl_${rowId}`, duration: 0 });
            await invoiceApi.createInvoiceFromOrder(targetOrderId, profileId);
            blob = await invoiceApi.downloadByOrderId(targetOrderId);
          } catch (createErr) {
            console.warn('Auto invoice creation failed:', createErr);
            if (item.id && item.id !== targetOrderId) {
              blob = await invoiceApi.downloadByOrderId(item.id);
            } else {
              throw err;
            }
          }
        } else if (item.id && item.id !== targetOrderId) {
          blob = await invoiceApi.downloadByOrderId(item.id);
        } else {
          throw err;
        }
      }

      if (!blob || blob.size === 0) {
        throw new Error('Received empty file from server');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoiceNum}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success({ content: `Invoice #${invoiceNum} downloaded!`, key: `dl_${rowId}` });
    } catch (err) {
      console.error('Single download error:', err);
      message.error({
        content: `Download failed: ${err.message || 'Invoice PDF not available on server'}`,
        key: `dl_${rowId}`,
        duration: 5
      });
    }
  };

  // ── Bulk Invoices Download (ZIP) ──
  const handleBulkDownload = async () => {
    if (selectedRows.size === 0) return;
    const idsArray = [...selectedRows];

    try {
      message.loading({ content: `Preparing bulk download for ${idsArray.length} invoices...`, key: 'bulk', duration: 0 });

      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      let successCount = 0;

      for (let i = 0; i < idsArray.length; i++) {
        const id = idsArray[i];
        const selectedOrder = invoices.find(item => item.id === id);
        const targetOrderId = selectedOrder?.orderId || id;
        const invoiceNum = selectedOrder?.invoiceNumber || selectedOrder?.invoiceNo || `Order_${targetOrderId}`;

        try {
          message.loading({ content: `Downloading invoice ${i + 1} of ${idsArray.length}...`, key: 'bulk', duration: 0 });
          let blob;
          try {
            blob = await invoiceApi.downloadByOrderId(targetOrderId);
          } catch (dlErr) {
            const isNotFound = dlErr.message && (
              dlErr.message.toLowerCase().includes('not found') ||
              dlErr.message.includes('400') ||
              dlErr.message.includes('404')
            );
            if (isNotFound) {
              const profileId = await getProfileId();
              await invoiceApi.createInvoiceFromOrder(targetOrderId, profileId);
              blob = await invoiceApi.downloadByOrderId(targetOrderId);
            } else {
              throw dlErr;
            }
          }
          if (blob && blob.size > 0) {
            zip.file(`Invoice_${invoiceNum}.pdf`, blob);
            successCount++;
          }
        } catch (error) {
          console.error(`Failed to download invoice for ID ${id}:`, error);
        }
      }

      if (successCount === 0) {
        message.error({ content: 'Failed to download selected invoices from server.', key: 'bulk' });
        return;
      }

      message.loading({ content: 'Creating ZIP file...', key: 'bulk', duration: 0 });
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoices_Bulk_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      message.success({ content: `${successCount} invoice(s) downloaded as ZIP!`, key: 'bulk' });
      setSelectedRows(new Set());
    } catch (err) {
      console.error('Bulk download error:', err);
      message.error({ content: 'Bulk download failed: ' + (err.message || 'Unknown error'), key: 'bulk' });
    }
  };

  // ── Excel Export ──
  const handleExportExcel = () => {
    try {
      message.loading({ content: 'Preparing Excel export...', key: 'excel' });

      const dataToExport = invoices.map(item => ({
        'Order Date': formatDate(item.createdAt || item.orderDate),
        'Invoice #': item.invoiceNumber || item.orderId || '—',
        'Customer Name': item.billedToName || item.customerName || '—',
        'Phone': item.billedToPhone || item.contactNumber || '',
        'Product': item.invoiceItems?.[0]?.productName || item.orderItems?.[0]?.productName || '—',
        'Amount': Number(item.grandTotal || item.totalAmount || 0),
        'Pay Mode': item.paymentMode || item.paymentType || '—',
        'Campaign': item.campaignName || item.teamName || item.leadSource || '—',
        'Status': item.invoiceStatus || item.status || '—'
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Billing List");

      const wscols = [
        { wch: 20 }, // Order Date
        { wch: 16 }, // Invoice #
        { wch: 25 }, // Customer Name
        { wch: 15 }, // Phone
        { wch: 30 }, // Product
        { wch: 14 }, // Amount
        { wch: 15 }, // Pay Mode
        { wch: 20 }, // Campaign
        { wch: 15 }  // Status
      ];
      worksheet['!cols'] = wscols;

      XLSX.writeFile(workbook, `Billing_List_${new Date().toISOString().split('T')[0]}.xlsx`);
      message.success({ content: 'Excel file downloaded!', key: 'excel' });
    } catch (err) {
      console.error('Excel export error:', err);
      message.error({ content: 'Failed to export Excel file.', key: 'excel' });
    }
  };

  // ── View Invoice Details ──
  const IMAGE_BASE_URL = `${ASSET_URL}/uploads/`;
  const handleViewInvoice = async (item) => {
    let fullInvoice = item;
    try {
      const targetId = item.orderId || item.id;
      const detail = await invoiceApi.getInvoiceByOrderId(targetId);
      if (detail) fullInvoice = detail;
    } catch (_) {
      try {
        const detail2 = await invoiceApi.getInvoiceById(item.id);
        if (detail2) fullInvoice = detail2;
      } catch (__) {}
    }

    const cp = fullInvoice.companyProfile || companyProfile;

    const terms = Array.isArray(cp?.instructionNotes) && cp.instructionNotes.length > 0
      ? cp.instructionNotes
          .filter(n => n.instructionType === 'Term and conditions')
          .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
          .map(n => n.instruction)
      : [
          "Refunds/returns follow the company's policy. Opened or used products are not eligible for return unless defective.",
          'Ayurvedic products are wellness supplements; results may vary.',
          'Placing an order means acceptance of these terms',
        ];

    const invoice = {
      id: fullInvoice.id,
      orderId: fullInvoice.orderId,
      invoiceNo: fullInvoice.invoiceNumber || fullInvoice.invoiceNo,
      invoiceDate: formatDate(fullInvoice.invoiceDate || fullInvoice.createdAt),
      subTotal: fullInvoice.subtotal || fullInvoice.subTotal || 0,
      advancePayable: fullInvoice.advancePayable || 0,
      grandTotal: fullInvoice.totalAmount || fullInvoice.grandTotal || 0,
      gstPercentage: fullInvoice.gstPercentage || 18,
      gstAmount: fullInvoice.gstAmount || 0,
      pendingAmount: (fullInvoice.totalAmount || fullInvoice.grandTotal || 0) - (fullInvoice.advancePayable || 0),
      company: {
        name: cp?.companyName || '',
        logoUrl: cp?.companyLogoUrl
          ? cp.companyLogoUrl.startsWith('http') ? cp.companyLogoUrl : IMAGE_BASE_URL + cp.companyLogoUrl
          : null,
        signatureUrl: cp?.signatureUrl
          ? cp.signatureUrl.startsWith('http') ? cp.signatureUrl : IMAGE_BASE_URL + cp.signatureUrl
          : null,
        upiScannerUrl: cp?.upiScannerUrl
          ? cp.upiScannerUrl.startsWith('http') ? cp.upiScannerUrl : IMAGE_BASE_URL + cp.upiScannerUrl
          : null,
        gstin: cp?.gstNumber || '',
        fssai: cp?.fssaiNumber || '',
        address: cp?.locality || '',
        phone: cp?.contactNumber || '',
        email: cp?.email || '',
        tagLine: cp?.tagLine || '',
      },
      billedTo: {
        name: fullInvoice.billedToName || fullInvoice.customerName || '',
        phone: fullInvoice.billedToPhone || fullInvoice.contactNumber || '',
        address: fullInvoice.billedToAddress || fullInvoice.deliveryAddress || '',
      },
      shippedTo: {
        name: fullInvoice.shippedToName || fullInvoice.billedToName || fullInvoice.customerName || '',
        phone: fullInvoice.shippedToPhone || fullInvoice.billedToPhone || fullInvoice.contactNumber || '',
        address: fullInvoice.shippedToAddress || fullInvoice.billedToAddress || fullInvoice.deliveryAddress || '',
      },
      items: fullInvoice.invoiceItems || fullInvoice.orderItems || [],
      terms,
    };

    setSelectedInvoice(invoice);
    setInvoiceView(true);
  };

  // Render InvoiceDetail view
  if (invoiceView && selectedInvoice) {
    return (
      <div className={`p-4 md:p-6 min-h-screen ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50/50'}`}>
        <InvoiceDetail
          invoice={selectedInvoice}
          onBack={() => { setInvoiceView(false); setSelectedInvoice(null); }}
          onDownload={() => handleSingleDownload(selectedInvoice)}
          currentTheme={currentTheme}
          isDark={isDark}
        />
      </div>
    );
  }

  return (
    <div className={`p-4 md:p-6 min-h-screen space-y-5 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50/30'}`}>
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Billing List
          </h1>
          <span className="text-gray-400 font-semibold text-lg desktop:text-2xl">
            ({totalInvoices.toLocaleString()})
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Tooltip title="Refresh billing list">
            <button
              onClick={handleRefresh}
              className={`p-2.5 desktop:p-3 border rounded-xl transition-all ${
                isDark ? 'bg-[#111827] border-white/10 hover:bg-white/5 text-gray-300' : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-600 shadow-sm'
              }`}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </Tooltip>

          <ExcelExportButton
            onClick={handleExportExcel}
            label="DOWNLOAD EXCEL"
          />
        </div>
      </div>

      {/* ── Bulk Selection Bar ── */}
      {selectedRows.size > 0 && (
        <div
          className="flex items-center justify-between px-5 py-3 rounded-2xl border shadow-sm transition-all"
          style={{
            backgroundColor: `${currentTheme.color}15`,
            borderColor: `${currentTheme.color}40`,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-sm"
              style={{ backgroundColor: currentTheme.color }}
            >
              {selectedRows.size}
            </div>
            <span className="text-sm font-bold" style={{ color: currentTheme.color }}>
              order{selectedRows.size > 1 ? 's' : ''} selected
            </span>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setSelectedRows(new Set())}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                isDark ? 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 shadow-sm'
              }`}
            >
              Clear
            </button>
            <button
              onClick={handleBulkDownload}
              className="px-4 py-1.5 text-xs font-bold text-white rounded-lg flex items-center gap-1.5 shadow-sm transition-all active:scale-95"
              style={{ backgroundColor: currentTheme.color }}
            >
              <Download className="w-3.5 h-3.5" />
              DOWNLOAD INVOICES (ZIP)
            </button>
          </div>
        </div>
      )}

      {/* ── Filters & Search Controls ── */}
      <div className="flex items-center space-x-4 flex-wrap gap-y-3">
        <ExpandableSearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          placeholder="Search by Order ID, Invoice #, or Customer Name"
        />

        <ThemeSelect
          value={`${pageSize} Orders`}
          onChange={handlePageSizeChange}
          options={pageSizeOptions.map(opt => `${opt} Orders`)}
          className="w-44 desktop:w-60"
        />
      </div>

      {/* ── Main Table Card ── */}
      <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-200'}`}>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-10 h-10 animate-spin mb-4" style={{ color: currentTheme.color }} />
            <p className="text-gray-500 text-sm font-medium">Loading billing list...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-24">
            <p className="text-red-500 mb-4 text-sm font-medium">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-5 py-2.5 text-white font-semibold rounded-xl text-sm shadow-sm transition-all"
              style={{ backgroundColor: currentTheme.color }}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1300px]">
                <thead className="text-white" style={{ background: currentTheme.color }}>
                  <tr>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-center w-12">
                      <button
                        onClick={toggleAll}
                        className="w-6 h-6 desktop:w-8 desktop:h-8 rounded border-2 flex items-center justify-center transition-colors"
                        style={{
                          backgroundColor: isAllSelected ? 'white' : 'transparent',
                          borderColor: isAllSelected ? 'white' : 'rgba(255, 255, 255, 0.5)'
                        }}
                        title={isAllSelected ? "Deselect all" : "Select all"}
                      >
                        {isAllSelected && <Check className="w-4 h-4 desktop:w-6 desktop:h-6" style={{ color: currentTheme.color }} />}
                      </button>
                    </th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Order Date</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Invoice #</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Customer Name</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Product</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Pay Mode</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Campaign</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-center text-xs desktop:text-base font-semibold uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-100'}`}>
                  {invoices.map((item, idx) => (
                    <tr
                      key={item.id || idx}
                      className={`transition-colors ${
                        selectedRows.has(item.id)
                          ? (isDark ? 'bg-white/[0.06]' : 'bg-teal-50/50')
                          : (isDark ? (idx % 2 === 0 ? 'bg-[#111827]' : 'bg-white/[0.02]') + ' hover:bg-white/5' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50') + ' hover:bg-gray-50')
                      }`}
                    >
                      {/* Checkbox */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-center">
                        <button
                          onClick={() => toggleRow(item.id)}
                          className="w-6 h-6 desktop:w-8 desktop:h-8 rounded border-2 flex items-center justify-center transition-colors"
                          style={{
                            backgroundColor: selectedRows.has(item.id) ? currentTheme.color : 'transparent',
                            borderColor: selectedRows.has(item.id) ? currentTheme.color : (isDark ? 'rgba(255,255,255,0.25)' : '#d1d5db')
                          }}
                        >
                          {selectedRows.has(item.id) && <Check className="w-3.5 h-3.5 desktop:w-5 desktop:h-5 text-white" />}
                        </button>
                      </td>

                      {/* Order Date */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {formatDate(item.createdAt || item.orderDate)}
                        </span>
                      </td>

                      {/* Invoice # */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <button
                          onClick={() => handleViewInvoice(item)}
                          className="text-sm desktop:text-base font-bold hover:underline whitespace-nowrap text-left"
                          style={{ color: currentTheme.color }}
                        >
                          {item.invoiceNumber || item.orderId || '—'}
                        </button>
                      </td>

                      {/* Name + Phone */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className={`text-sm desktop:text-base font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                            {item.billedToName || item.customerName || '—'}
                          </span>
                          <div className="flex items-center space-x-1.5 mt-0.5">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            <span className={`text-xs desktop:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {maskPhone(item.billedToPhone || item.contactNumber || '')}
                            </span>
                            {(item.billedToPhone || item.contactNumber) && (
                              <span className="w-4 h-4 rounded-full flex items-center justify-center bg-[#25D366] ml-1">
                                <MessageCircle className="w-2.5 h-2.5 text-white" />
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Product */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate block max-w-[180px]`}
                          title={item.invoiceItems?.[0]?.productName || item.orderItems?.[0]?.productName || '—'}
                        >
                          {item.invoiceItems?.[0]?.productName || item.orderItems?.[0]?.productName || '—'}
                        </span>
                      </td>

                      {/* Amount */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className="text-sm desktop:text-base font-bold" style={{ color: currentTheme.color }}>
                          ₹ {Number(item.grandTotal || item.totalAmount || 0).toLocaleString('en-IN')}
                        </span>
                      </td>

                      {/* Pay Mode */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-xs desktop:text-sm font-semibold px-2.5 py-1 rounded-lg border ${
                          isDark ? 'bg-white/5 text-gray-300 border-white/10' : 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}>
                          {item.paymentMode || item.paymentType || '—'}
                        </span>
                      </td>

                      {/* Campaign */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className={`text-sm desktop:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {item.campaignName || item.teamName || item.leadSource || '—'}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <StatusBadge status={item.invoiceStatus || item.status} isDark={isDark} />
                      </td>

                      {/* Action */}
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <Tooltip title="View Invoice Details">
                            <button
                              onClick={() => handleViewInvoice(item)}
                              className={`p-2 desktop:p-2.5 rounded-xl transition-all ${
                                isDark ? 'hover:bg-white/10 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                              }`}
                              style={{ color: currentTheme.color }}
                            >
                              <Eye className="w-4 h-4 desktop:w-5 desktop:h-5" />
                            </button>
                          </Tooltip>

                          <Tooltip title="Download Invoice PDF">
                            <button
                              onClick={() => handleSingleDownload(item)}
                              className={`p-2 desktop:p-2.5 rounded-xl transition-all ${
                                isDark ? 'hover:bg-white/10 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                              }`}
                              style={{ color: currentTheme.color }}
                            >
                              <Download className="w-4 h-4 desktop:w-5 desktop:h-5" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {invoices.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-4 py-16 text-center text-gray-500">
                        <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                        <p className="text-base font-semibold">No invoices found</p>
                        <p className="text-xs text-gray-400 mt-1">Try adjusting your search criteria</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            <Pagination
              currentPage={currentPage}
              totalItems={totalInvoices}
              itemsPerPage={pageSize}
              onPageChange={(page) => {
                setCurrentPage(page);
                fetchData(page, searchTerm, pageSize);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default BillingList;